import React, { useCallback, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Flyout, FlyoutTitle } from 'components/Flyout';
import { FlyoutContext } from 'components/Flyout/FlyoutContext';
import Message from 'components/Message';
import { createSuccessSnackbar } from 'components/Snackbar';
import { attachedGroupIdsForItem, planGroupLinksForItem } from 'features/Sku/Modifiers/Modifiers.savePlan';
import useModifierGroups from 'features/Sku/Modifiers/useModifierGroups';
import useModifierItemLinks from 'features/Sku/Modifiers/useModifierItemLinks';
import { bem } from 'helpers/class-name';
import { reportError } from 'helpers/error-logging';
import { getNextVenueMidnight } from 'helpers/intl-fns';
import { makePath } from 'helpers/link-generation';
import Segment from 'helpers/segment';
import { selectActiveVenue } from 'selectors/general';
import { selectLocale } from 'selectors/locale-data';
import { selectIsMobile } from 'selectors/ui';

import { getIsTaxExcludedMenuPrice } from 'constants/logic';
import { openModal } from 'components/Modal';
import { ITEM_SAVE_STATUS, STATUS_TO_BOOLEANS } from '../SkuItems.constants';
import {
  getCategoryId,
  getCategoryMenuId,
  getItemCategoryIds,
  getItemId,
  getItemName,
  getItemPayoutRate,
} from '../SkuMenuData.normalizers';
import { getCategoryRelationDelta } from '../SkuCategoryRelations.helpers';
import useSkuCategories from '../useSkuCategories';
import useSkuMutations from '../useSkuMutations';
import useSkuPricingSubmission from '../useSkuPricingSubmission';
import { getSkuVenueSource } from '../SkuVenueSource.helpers';
import { SKU_ANALYTICS_EVENTS } from '../SkuAnalytics.constants';
import { ADD_CATEGORY_MODAL_NAME } from './AddCategoryModal';
import SkuEditItemFormContent from './SkuEditItemFormContent';
import { messages } from './SkuEditItemForm.messages';

import './SkuItemForm.scss';
import './SkuAddItemForm.scss';

const { block } = bem('item-form');

export const SKU_EDIT_ITEM_FLYOUT_ID = 'sku-edit-item-flyout';

const SkuEditItemForm = ({
  isLoading,
  currencyCode,
  item,
  preselectedCategoryIds,
  categoriesRefreshKey,
  onSuccess,
  isReadOnly,
}) => {
  const dispatch = useDispatch();
  const isMobile = useSelector(selectIsMobile);
  const locale = useSelector(selectLocale);
  const venue = useSelector(selectActiveVenue);
  const { closeFlyout } = useContext(FlyoutContext);

  const { venueId, countryCodeShort, timezone } = venue;
  const source = getSkuVenueSource(venue);
  const itemId = getItemId(item);
  const itemName = getItemName(item);
  const payoutRate = getItemPayoutRate(item);
  const [localRefreshKey, setLocalRefreshKey] = useState(0);
  const [saveFailure, setSaveFailure] = useState(null);

  const baseCategoryIds = useMemo(() => {
    if (preselectedCategoryIds?.length) {
      return preselectedCategoryIds;
    }
    return getItemCategoryIds(item);
  }, [item, preselectedCategoryIds]);

  const { groups, subgroups, categoriesLoading, preselectedCategories } = useSkuCategories(
    venueId,
    baseCategoryIds,
    categoriesRefreshKey + localRefreshKey
  );

  const { canAuthorModifierLinks, canViewModifierLinks, submitGroupLinksForItem } = useModifierItemLinks({ venueId });
  const canEditModifiers = canAuthorModifierLinks && !isReadOnly;
  // An integrated partner cannot attach anything, but the modifiers their POS put on this item are
  // still theirs to see, so the section is fetched and rendered for them too, just not as a picker.
  const showModifiersSection = canViewModifierLinks;
  const modifierGroupsQuery = useModifierGroups(showModifiersSection ? venueId : undefined);
  const handleRetryModifierGroups = useCallback(() => modifierGroupsQuery.refetch(), [modifierGroupsQuery]);
  const handleDismissSaveFailure = useCallback(() => setSaveFailure(null), []);
  const modifierGroups = modifierGroupsQuery.data || [];
  // isInitialLoading, not isLoading: a disabled query reports loading forever. Folding this into
  // the form's isLoading below keeps the form unmounted until the fetch resolves, because the
  // resolve changes formKey and a remount mid-edit would wipe the partner's typing.
  const modifierGroupsLoading = showModifiersSection && modifierGroupsQuery.isInitialLoading;
  const baseModifierGroupIds = useMemo(
    () => attachedGroupIdsForItem(modifierGroupsQuery.data || [], itemId),
    [modifierGroupsQuery.data, itemId]
  );

  const isTaxExcludedMenuPrice = getIsTaxExcludedMenuPrice(countryCodeShort);
  const menuPriceDescription = isTaxExcludedMenuPrice
    ? messages.taxExcludedMenuPriceDescription
    : messages.taxIncludedMenuPriceDescription;

  const formKey = useMemo(
    () =>
      `${itemId || 'no-item'}::${(baseCategoryIds || []).join(',')}::${subgroups.length}::${baseModifierGroupIds.join(
        ','
      )}`,
    [itemId, baseCategoryIds, subgroups.length, baseModifierGroupIds]
  );

  const { editItem } = useSkuMutations({ venueId });

  const { handleSubmitPricing } = useSkuPricingSubmission({
    venue,
    currencyCode,
    onSuccess,
    errorLabel: 'SKU Item edit',
  });

  const navigate = useNavigate();

  const handleAddModifierGroup = useCallback(() => {
    closeFlyout();
    navigate(makePath.skuModifierAdd({ venueId }));
  }, [closeFlyout, navigate, venueId]);

  const handleAddCategory = useCallback(() => {
    const defaultMenuId = baseCategoryIds?.length
      ? getCategoryMenuId(subgroups.find(s => getCategoryId(s) === baseCategoryIds[0]))
      : undefined;

    dispatch(
      openModal(ADD_CATEGORY_MODAL_NAME, {
        menus: groups,
        venueId,
        defaultMenuId,
        onSuccess: () => setLocalRefreshKey(prev => prev + 1),
      })
    );
  }, [dispatch, groups, venueId, baseCategoryIds, subgroups]);

  const handleSubmit = useCallback(
    async values => {
      const {
        itemName: nextName,
        itemDescription: nextDescription,
        categories,
        availabilityStatus: nextAvailabilityStatus,
        menuPrice: price,
        linkToMenu: link,
      } = values;
      const categoryEntries = Array.isArray(categories) ? categories : preselectedCategories;

      const { enabled, soldOut, partnerState } = STATUS_TO_BOOLEANS[nextAvailabilityStatus] || {
        enabled: true,
        soldOut: false,
        partnerState: null,
      };
      const flipBackAt = soldOut ? getNextVenueMidnight(timezone) : undefined;

      try {
        await editItem.mutateAsync({
          itemId,
          optimistic: {
            name: nextName.trim(),
            description: nextDescription.trim(),
            enabled,
            soldOut,
            partnerState,
          },
          payload: {
            name: nextName.trim(),
            description: nextDescription.trim(),
            enabled,
            soldOut,
            partnerState,
            ...(flipBackAt !== undefined ? { flipBackAt } : {}),
          },
          baseCategoryIds,
          nextCategories: categoryEntries,
        });

        const { removedCategoryIds, addedCategoryIds } = getCategoryRelationDelta({
          previousCategoryIds: baseCategoryIds,
          nextCategories: categoryEntries,
        });
        removedCategoryIds.forEach(categoryId => {
          Segment.event(SKU_ANALYTICS_EVENTS.CATEGORY_ITEM_REMOVED, { venueId, categoryId, itemId, source });
        });
        addedCategoryIds.forEach(categoryId => {
          Segment.event(SKU_ANALYTICS_EVENTS.CATEGORY_ITEM_ADDED, { venueId, categoryId, itemId, source });
        });

        if (canEditModifiers) {
          const { linked, unlinked } = planGroupLinksForItem({
            groups: modifierGroups,
            itemId,
            selectedGroupIds: values.modifierGroupIds,
          });
          const groupOutcome = await submitGroupLinksForItem({ itemId, linked, unlinked });
          if (groupOutcome.failedGroupIds.length > 0) {
            setSaveFailure({ status: ITEM_SAVE_STATUS.PARTIALLY_SAVED, modifiersFailed: true });
            return;
          }
        }
        setSaveFailure(null);

        if (price && link) {
          const pricingSucceeded = await handleSubmitPricing({ name: nextName, price, link });
          if (!pricingSucceeded) return;
        }

        Segment.event(SKU_ANALYTICS_EVENTS.ITEM_EDITED, { venueId, itemId, source });

        onSuccess?.({
          itemId,
          itemName: nextName,
          menuPrice: price,
          linkToMenu: link,
        });
        closeFlyout();
        dispatch(
          createSuccessSnackbar(
            messages.successEditItem,
            {
              itemName: nextName,
              strong: chunks => <strong>{chunks}</strong>,
            },
            { isLowStyle: true }
          )
        );
      } catch (error) {
        reportError('Error editing SKU item', error);
        setSaveFailure({ status: ITEM_SAVE_STATUS.NOTHING_SAVED, stateUnknown: true });
      }
    },
    [
      preselectedCategories,
      editItem,
      handleSubmitPricing,
      submitGroupLinksForItem,
      canEditModifiers,
      modifierGroups,
      onSuccess,
      itemId,
      baseCategoryIds,
      closeFlyout,
      dispatch,
      timezone,
    ]
  );

  return (
    <Flyout
      id={SKU_EDIT_ITEM_FLYOUT_ID}
      className={`${block()} sku-add-item-flyout sku-edit-item-flyout`}
      onClose={closeFlyout}
      overrideOutsideClick={() => true}
      showBackButton={isMobile}>
      <FlyoutTitle>
        <Message message={isReadOnly ? messages.viewItem : messages.editItem} values={{ itemName }} />
      </FlyoutTitle>
      <SkuEditItemFormContent
        isReadOnly={isReadOnly}
        isLoading={isLoading || modifierGroupsLoading}
        categoriesLoading={categoriesLoading}
        groups={groups}
        subgroups={subgroups}
        currencyCode={currencyCode}
        locale={locale}
        item={item}
        itemName={itemName}
        payoutRate={payoutRate}
        preselectedCategories={preselectedCategories}
        menuPriceDescription={menuPriceDescription}
        formKey={formKey}
        onSubmit={handleSubmit}
        onAddCategory={handleAddCategory}
        modifierGroups={modifierGroups}
        modifierGroupsLoading={modifierGroupsLoading}
        modifierGroupsLoadFailed={modifierGroupsQuery.isError}
        onRetryModifierGroups={handleRetryModifierGroups}
        preselectedModifierGroupIds={baseModifierGroupIds}
        onAddModifierGroup={handleAddModifierGroup}
        showModifiersSection={showModifiersSection}
        canEditModifiers={canEditModifiers}
        saveFailure={saveFailure}
        onDismissSaveFailure={handleDismissSaveFailure}
      />
    </Flyout>
  );
};

SkuEditItemForm.propTypes = {
  isLoading: PropTypes.bool,
  currencyCode: PropTypes.string.isRequired,
  item: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    menuPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    linkToMenu: PropTypes.string,
    enabled: PropTypes.bool,
    soldOut: PropTypes.bool,
  }),
  preselectedCategoryIds: PropTypes.arrayOf(PropTypes.string),
  categoriesRefreshKey: PropTypes.number,
  onSuccess: PropTypes.func,
  isReadOnly: PropTypes.bool,
};

SkuEditItemForm.defaultProps = {
  isLoading: false,
  item: null,
  preselectedCategoryIds: [],
  categoriesRefreshKey: 0,
  onSuccess: null,
};

export default SkuEditItemForm;
